import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import get from 'lodash.get';
import { DateTime } from 'luxon';
import { mapRateScheduleEntries } from '~/modules/rateCard/enhancers/mapping-util';
import { getTodayForUser } from '~/modules/common/dates/today';

export const GET_EFFECTIVE_PROJECT_RATES = gql`
  query getEffectiveRates(
    $projectId: String!
    $roleRatePage: Int
    $roleRatePageSize: Int
    $resourceRatePage: Int
    $resourceRatePageSize: Int
    $effectiveDateRange: DateRangeInput2
    $effectiveDateRangeInput: DateRangeInput!
  ) {
    project(projectId: $projectId) {
      id
      projectRate: projectRate2(effectiveDateRange: $effectiveDateRange) {
        id
        effectiveBillingRate(effectiveDateRange: $effectiveDateRangeInput) {
          effectiveDate: effectiveDate2
          amount {
            currency {
              id
              symbol
              displayText
            }
            amount
          }
        }
      }
      roleRates: roleRates2(
        page: $roleRatePage
        pageSize: $roleRatePageSize
        effectiveDateRange: $effectiveDateRange
      ) {
        id
        roleReference {
          id
          displayText
        }
        effectiveBillingRate(effectiveDateRange: $effectiveDateRangeInput) {
          effectiveDate: effectiveDate2
          amount {
            currency {
              id
              symbol
              displayText
            }
            amount
          }
        }
      }
      roleRatesCount: roleRatesCount2(effectiveDateRange: $effectiveDateRange)
      userRatesCount: userRatesCount2(effectiveDateRange: $effectiveDateRange)
      userRates: userRates2(
        page: $resourceRatePage
        pageSize: $resourceRatePageSize
        effectiveDateRange: $effectiveDateRange
      ) {
        id
        userReference {
          id
          displayText
        }
        effectiveBillingRate(effectiveDateRange: $effectiveDateRangeInput) {
          effectiveDate: effectiveDate2
          amount {
            currency {
              id
              symbol
              displayText
            }
            amount
          }
        }
      }
    }
  }
`;

export const GET_EFFECTIVE_PROJECT_RATES_COUNT = gql`
  query getEffectiveRates($projectId: String!) {
    project(projectId: $projectId) {
      id
      projectRate: projectRate2 {
        id
        billingScheduleEntries {
          effectiveDate: effectiveDate2
          amount {
            currency {
              id
              symbol
              displayText: symbol
            }
            amount
          }
        }
      }
      userRates: userRates2 {
        id
        billingScheduleEntries {
          effectiveDate: effectiveDate2
          amount {
            currency {
              id
              symbol
              displayText: symbol
            }
            amount
          }
        }
      }
      roleRates: roleRates2 {
        id
        billingScheduleEntries {
          effectiveDate: effectiveDate2
          amount {
            currency {
              id
              symbol
              displayText: symbol
            }
            amount
          }
        }
      }
      roleRatesCount: roleRatesCount2
      userRatesCount: userRatesCount2
    }
  }
`;

const getToday = me => {
  const { day, month, year } = getTodayForUser(me);

  return DateTime.utc(year, month, day);
};

const loadMoreRows = async ({ rateTypeKey, fetchMore, variables, rates }) => {
  await fetchMore({
    query: GET_EFFECTIVE_PROJECT_RATES,
    variables: {
      ...variables,
      roleRatePage:
        rateTypeKey === 'roleRates'
          ? Math.ceil(rates.length / variables.roleRatePageSize) + 1
          : -1,
      resourceRatePage:
        rateTypeKey === 'userRates'
          ? Math.ceil(rates.length / variables.resourceRatePageSize) + 1
          : -1
    },
    updateQuery: (previousResult, { fetchMoreResult: { project } }) => ({
      ...previousResult,
      project: {
        ...previousResult.project,
        [rateTypeKey]: [
          ...previousResult.project[rateTypeKey],
          ...project[rateTypeKey]
        ]
      }
    })
  });
};

export const useProjectRates = ({
  projectId,
  roleRatePage = 1,
  roleRatePageSize = 10,
  resourceRatePage = 1,
  resourceRatePageSize = 10,
  rateTypeKey,
  effectiveDateRange,
  context,
  me
}) => {
  const today = getToday(me);
  const { data, loading, fetchMore, variables, error, refetch } = useQuery(
    GET_EFFECTIVE_PROJECT_RATES,
    {
      variables: {
        projectId,
        roleRatePage: rateTypeKey === 'roleRates' ? roleRatePage : -1,
        roleRatePageSize,
        resourceRatePage: rateTypeKey === 'userRates' ? resourceRatePage : -1,
        resourceRatePageSize,
        effectiveDateRange: effectiveDateRange || {
          startDate: today,
          endDate: today
        },
        effectiveDateRangeInput: effectiveDateRange || {
          startDate: today,
          endDate: today
        }
      },
      skip: context === 'client',
      errorPolicy: 'all',
      fetchPolicy: 'network-only'
    }
  );

  const rates = get(data, `project.${rateTypeKey}`, []);
  const count = get(data, `project.${rateTypeKey}Count`, 0);

  return {
    rates: loading
      ? []
      : rateTypeKey === 'projectRate'
      ? rates
        ? [rates].map(mapRateScheduleEntries(false))
        : []
      : (rates || []).map(mapRateScheduleEntries(false)),
    hasMoreRows: rates && rates.length < count,
    count,
    loadMoreRows: useCallback(
      () =>
        loadMoreRows({
          rateTypeKey,
          fetchMore,
          variables,
          rates
        }),
      [fetchMore, rateTypeKey, rates, variables]
    ),
    hasServiceError: Boolean(error),
    isProjectRatesLoading: loading,
    refetchProjectRates: refetch
  };
};
